import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { campaignService } from "../config/campaignApi";

const CampaignMonitorPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Auth
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const ACCESS_PASSWORD = "PASSword123$";

  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);

  // CSV Upload
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Test Send
  const [testEmail, setTestEmail] = useState("");
  const [testLoading, setTestLoading] = useState(false);

  // Activity
  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);

  // ─── Auth ───
  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ACCESS_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError("");
      sessionStorage.setItem("nota_campaign_auth", "true");
    } else {
      setAuthError("Invalid password. Please try again.");
      setPassword("");
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem("nota_campaign_auth") === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("nota_campaign_auth");
    setPassword("");
  };

  // ─── Flash messages ───
  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  // ─── Stats ───
  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const response = await campaignService.getStats();
      setStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchStats]);

  // ─── Settings ───
  const fetchSettings = async () => {
    try {
      const response = await campaignService.getSettings();
      setSettings(response.data);
      setSettingsForm({
        fromEmail: response.data.fromEmail,
        fromName: response.data.fromName,
        batchSize: response.data.batchSize,
        delayMinMs: response.data.delayMinMs,
        delayMaxMs: response.data.delayMaxMs,
        cronInterval: response.data.cronInterval,
        dailyLimit: response.data.dailyLimit,
      });
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchSettings();
  }, [isAuthenticated]);

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const updates = {
        fromEmail: settingsForm.fromEmail,
        fromName: settingsForm.fromName,
        batchSize: parseInt(settingsForm.batchSize),
        delayMinMs: parseInt(settingsForm.delayMinMs),
        delayMaxMs: parseInt(settingsForm.delayMaxMs),
        cronInterval: parseInt(settingsForm.cronInterval),
        dailyLimit: parseInt(settingsForm.dailyLimit),
      };
      const response = await campaignService.updateSettings(updates);
      setSettings(response.data);
      showSuccess("Settings saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  // ─── Campaign Controls ───
  const handleStart = async () => {
    setActionLoading(true);
    try {
      const response = await campaignService.start();
      setStats(response.data);
      showSuccess("Campaign started");
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      const response = await campaignService.stop();
      setStats(response.data);
      showSuccess("Campaign stopped");
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryFailed = async () => {
    if (
      !window.confirm(
        "Reset all failed records (with < 3 attempts) back to pending?",
      )
    )
      return;
    setActionLoading(true);
    try {
      const response = await campaignService.retryFailed();
      setStats(response.data);
      showSuccess(response.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearQueue = async () => {
    if (
      !window.confirm(
        "This will delete ALL records from the queue. This cannot be undone. Continue?",
      )
    )
      return;
    setActionLoading(true);
    try {
      const response = await campaignService.clearQueue();
      showSuccess(response.message);
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ─── CSV Upload ───
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setCsvPreview(null);
      setImportResult(null);
    }
  };

  const handlePreview = async () => {
    if (!csvFile) return;
    setUploadLoading(true);
    try {
      const response = await campaignService.uploadCsv(csvFile, true);
      setCsvPreview(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleImport = async () => {
    if (!csvFile) return;
    if (!window.confirm(`Import records from ${csvFile.name}?`)) return;
    setUploadLoading(true);
    try {
      const response = await campaignService.uploadCsv(csvFile, false);
      setImportResult(response.data);
      setCsvPreview(null);
      setCsvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      showSuccess(response.message);
      fetchStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Test Send ───
  const handleTestSend = async () => {
    if (!testEmail) return;
    setTestLoading(true);
    try {
      const response = await campaignService.testSend(testEmail);
      if (response.success) {
        showSuccess(`Test email sent to ${testEmail}`);
        setTestEmail("");
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setTestLoading(false);
    }
  };

  // ─── Activity ───
  const fetchActivity = async () => {
    try {
      const response = await campaignService.getActivity(30);
      setActivity(response.data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (activityOpen && isAuthenticated) fetchActivity();
  }, [activityOpen, isAuthenticated]);

  // ─── Computed ───
  const progressPercent = stats
    ? Math.round((stats.sent / Math.max(stats.total, 1)) * 100)
    : 0;
  const dailyPercent = stats
    ? Math.round((stats.sentToday / Math.max(stats.dailyLimit, 1)) * 100)
    : 0;

  const getStatusColor = () => {
    if (!stats) return "bg-gray-400";
    if (stats.pending === 0 && stats.total > 0) return "bg-blue-500";
    if (stats.isRunning) return "bg-green-500";
    return "bg-yellow-500";
  };

  const getStatusText = () => {
    if (!stats) return "Loading...";
    if (stats.pending === 0 && stats.total > 0) return "Completed";
    if (stats.isRunning) return "Running";
    return "Paused";
  };

  // ─── Auth Gate ───
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <motion.div
          className="bg-white rounded-lg shadow-md p-8 max-w-md w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-[#202356] mb-2">
              Campaign Monitor
            </h2>
            <p className="text-gray-600">
              Enter the password to access the campaign dashboard.
            </p>
          </div>
          <form onSubmit={handleAuth}>
            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                placeholder="Enter access password"
                required
              />
            </div>
            {authError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                {authError}
              </div>
            )}
            <button
              type="submit"
              className="w-full bg-[#202356] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
            >
              Access Dashboard
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ─── Main Dashboard ───
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="py-12 px-6 md:px-12 bg-[#202356]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-white mb-2">
                  Campaign Monitor
                </h1>
                <p className="text-lg text-white opacity-90">
                  Australia Care Workforce Email Campaign
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full ${getStatusColor()} animate-pulse`}
                  ></span>
                  <span className="text-white font-medium">
                    {getStatusText()}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-200 transition-colors text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Flash Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-700 hover:text-red-900 font-bold"
              >
                &times;
              </button>
            </div>
          )}
          {successMsg && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex justify-between items-center">
              <span>{successMsg}</span>
              <button
                onClick={() => setSuccessMsg(null)}
                className="text-green-700 hover:text-green-900 font-bold"
              >
                &times;
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356]"></div>
            </div>
          ) : stats ? (
            <>
              {/* Overall Progress */}
              <motion.div
                className="bg-white rounded-lg shadow-md p-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Overall Progress
                </h2>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">
                    {stats.sent.toLocaleString()} /{" "}
                    {stats.total.toLocaleString()} emails sent
                  </span>
                  <span className="text-sm font-medium text-[#202356]">
                    {progressPercent}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-[#2542D4] h-4 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
              </motion.div>

              {/* Stats Cards */}
              <motion.div
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="bg-white rounded-lg shadow-md p-5">
                  <p className="text-sm text-gray-500 mb-1">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {stats.pending.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-5">
                  <p className="text-sm text-gray-500 mb-1">Sent</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.sent.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-5">
                  <p className="text-sm text-gray-500 mb-1">Failed</p>
                  <p className="text-2xl font-bold text-red-600">
                    {stats.failed.toLocaleString()}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-5">
                  <p className="text-sm text-gray-500 mb-1">Total</p>
                  <p className="text-2xl font-bold text-[#202356]">
                    {stats.total.toLocaleString()}
                  </p>
                </div>
              </motion.div>

              {/* Daily Usage + Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Daily Usage */}
                <motion.div
                  className="bg-white rounded-lg shadow-md p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    Today's Usage
                  </h2>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">
                      {stats.sentToday.toLocaleString()} /{" "}
                      {stats.dailyLimit.toLocaleString()}
                    </span>
                    <span className="text-sm font-medium text-[#202356]">
                      {dailyPercent}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${dailyPercent >= 90 ? "bg-red-500" : dailyPercent >= 70 ? "bg-yellow-500" : "bg-green-500"}`}
                      style={{ width: `${Math.min(dailyPercent, 100)}%` }}
                    ></div>
                  </div>
                  {dailyPercent >= 100 && (
                    <p className="text-sm text-red-600 mt-2">
                      Daily limit reached. Resuming tomorrow.
                    </p>
                  )}
                  <p className="mt-4 text-sm text-gray-500">
                    Estimated days remaining:{" "}
                    {stats.pending > 0
                      ? Math.ceil(stats.pending / stats.dailyLimit)
                      : 0}
                  </p>
                </motion.div>

                {/* Campaign Controls */}
                <motion.div
                  className="bg-white rounded-lg shadow-md p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    Campaign Controls
                  </h2>
                  <div className="space-y-3">
                    {stats.isRunning ? (
                      <button
                        onClick={handleStop}
                        disabled={actionLoading}
                        className="w-full py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {actionLoading ? "Processing..." : "Stop Campaign"}
                      </button>
                    ) : (
                      <button
                        onClick={handleStart}
                        disabled={actionLoading || stats.pending === 0}
                        className="w-full py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {actionLoading ? "Processing..." : "Start Campaign"}
                      </button>
                    )}
                    <button
                      onClick={handleRetryFailed}
                      disabled={actionLoading || stats.failed === 0}
                      className="w-full py-2 px-4 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors disabled:opacity-50"
                    >
                      {actionLoading
                        ? "Processing..."
                        : `Retry Failed (${stats.failed})`}
                    </button>
                    <div className="flex gap-3">
                      <button
                        onClick={fetchStats}
                        disabled={actionLoading}
                        className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        Refresh
                      </button>
                      <a
                        href={campaignService.getExportUrl()}
                        download
                        className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-center"
                      >
                        Export CSV
                      </a>
                    </div>
                    <button
                      onClick={handleClearQueue}
                      disabled={actionLoading || stats.total === 0}
                      className="w-full py-2 px-4 bg-gray-200 text-red-600 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 text-sm"
                    >
                      Clear Queue
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-4">
                    Stats auto-refresh every 30 seconds
                  </p>
                </motion.div>
              </div>

              {/* Processing Indicator */}
              {stats.isProcessing && (
                <motion.div
                  className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2542D4]"></div>
                    <span className="text-blue-800 font-medium">
                      Currently processing a batch... Emails are being sent with
                      delays between sends.
                    </span>
                  </div>
                </motion.div>
              )}

              {/* CSV Upload */}
              <motion.div
                className="bg-white rounded-lg shadow-md p-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Upload CSV
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  CSV must have columns: name, email, company, sent (optional).
                  Rows with sent=yes will be skipped.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-[#202356] file:text-white hover:file:bg-opacity-90"
                  />
                  <button
                    onClick={handlePreview}
                    disabled={!csvFile || uploadLoading}
                    className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    {uploadLoading ? "Processing..." : "Preview"}
                  </button>
                </div>

                {/* Preview */}
                {csvPreview && (
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    <h3 className="font-medium text-gray-800 mb-3">
                      Import Preview
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                      <div className="text-sm">
                        <span className="text-gray-500">Total rows:</span>{" "}
                        <span className="font-medium">
                          {csvPreview.totalRows}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Already sent:</span>{" "}
                        <span className="font-medium">
                          {csvPreview.alreadySent}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">To import:</span>{" "}
                        <span className="font-medium text-green-600">
                          {csvPreview.toImport}
                        </span>
                      </div>
                    </div>
                    {csvPreview.companies && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Companies:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(csvPreview.companies).map(
                            ([company, count]) => (
                              <span
                                key={company}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {company}: {count}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                    {csvPreview.sampleRows &&
                      csvPreview.sampleRows.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-500 mb-1">
                            Sample rows:
                          </p>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-1 pr-4">Name</th>
                                  <th className="text-left py-1 pr-4">Email</th>
                                  <th className="text-left py-1">Company</th>
                                </tr>
                              </thead>
                              <tbody>
                                {csvPreview.sampleRows.map((row, i) => (
                                  <tr
                                    key={i}
                                    className="border-b border-gray-100"
                                  >
                                    <td className="py-1 pr-4">{row.name}</td>
                                    <td className="py-1 pr-4">{row.email}</td>
                                    <td className="py-1">{row.company}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    <button
                      onClick={handleImport}
                      disabled={uploadLoading}
                      className="py-2 px-6 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {uploadLoading
                        ? "Importing..."
                        : `Import ${csvPreview.toImport} Records`}
                    </button>
                  </div>
                )}

                {/* Import Result */}
                {importResult && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                    <h3 className="font-medium text-green-800 mb-2">
                      Import Complete
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Total rows:</span>{" "}
                        {importResult.totalRows}
                      </div>
                      <div>
                        <span className="text-gray-500">Skipped (sent):</span>{" "}
                        {importResult.skippedSent}
                      </div>
                      <div>
                        <span className="text-gray-500">
                          Skipped (invalid):
                        </span>{" "}
                        {importResult.skippedInvalid}
                      </div>
                      <div>
                        <span className="text-gray-500">Duplicates:</span>{" "}
                        {importResult.duplicates}
                      </div>
                      <div>
                        <span className="text-gray-500">Already in queue:</span>{" "}
                        {importResult.alreadyInQueue}
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Inserted: {importResult.inserted}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Test Send */}
              <motion.div
                className="bg-white rounded-lg shadow-md p-6 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Test Email
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  Send a test email to verify formatting and deliverability
                  before starting the campaign.
                </p>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent"
                    placeholder="Enter test email address"
                  />
                  <button
                    onClick={handleTestSend}
                    disabled={!testEmail || testLoading}
                    className="py-2 px-6 bg-[#2542D4] text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
                  >
                    {testLoading ? "Sending..." : "Send Test"}
                  </button>
                </div>
              </motion.div>

              {/* Settings (Collapsible) */}
              <motion.div
                className="bg-white rounded-lg shadow-md mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <button
                  onClick={() => {
                    setSettingsOpen(!settingsOpen);
                    if (!settingsOpen && !settings) fetchSettings();
                  }}
                  className="w-full p-6 flex justify-between items-center text-left"
                >
                  <h2 className="text-lg font-semibold text-gray-800">
                    Settings
                  </h2>
                  <span className="text-gray-400">
                    {settingsOpen ? "▲" : "▼"}
                  </span>
                </button>
                {settingsOpen && settings && (
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          From Email
                        </label>
                        <input
                          type="email"
                          value={settingsForm.fromEmail || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              fromEmail: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          From Name
                        </label>
                        <input
                          type="text"
                          value={settingsForm.fromName || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              fromName: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Batch Size
                        </label>
                        <input
                          type="number"
                          value={settingsForm.batchSize || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              batchSize: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Daily Limit
                        </label>
                        <input
                          type="number"
                          value={settingsForm.dailyLimit || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              dailyLimit: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Delay (ms)
                        </label>
                        <input
                          type="number"
                          value={settingsForm.delayMinMs || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              delayMinMs: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Delay (ms)
                        </label>
                        <input
                          type="number"
                          value={settingsForm.delayMaxMs || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              delayMaxMs: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cron Interval (minutes)
                        </label>
                        <input
                          type="number"
                          value={settingsForm.cronInterval || ""}
                          onChange={(e) =>
                            setSettingsForm({
                              ...settingsForm,
                              cronInterval: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#202356] focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleSaveSettings}
                      disabled={settingsLoading}
                      className="py-2 px-6 bg-[#202356] text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
                    >
                      {settingsLoading ? "Saving..." : "Save Settings"}
                    </button>
                  </div>
                )}
              </motion.div>

              {/* Activity Log (Collapsible) */}
              <motion.div
                className="bg-white rounded-lg shadow-md mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <button
                  onClick={() => {
                    setActivityOpen(!activityOpen);
                  }}
                  className="w-full p-6 flex justify-between items-center text-left"
                >
                  <h2 className="text-lg font-semibold text-gray-800">
                    Recent Activity
                  </h2>
                  <div className="flex items-center gap-2">
                    {activityOpen && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchActivity();
                        }}
                        className="text-sm text-[#2542D4] hover:underline"
                      >
                        Refresh
                      </button>
                    )}
                    <span className="text-gray-400">
                      {activityOpen ? "▲" : "▼"}
                    </span>
                  </div>
                </button>
                {activityOpen && (
                  <div className="px-6 pb-6">
                    {activity.length === 0 ? (
                      <p className="text-sm text-gray-500">No activity yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 pr-4">Name</th>
                              <th className="text-left py-2 pr-4">Email</th>
                              <th className="text-left py-2 pr-4">Status</th>
                              <th className="text-left py-2 pr-4">Time</th>
                              <th className="text-left py-2">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activity.map((item, i) => (
                              <tr key={i} className="border-b border-gray-100">
                                <td className="py-2 pr-4">{item.name}</td>
                                <td className="py-2 pr-4 text-gray-600">
                                  {item.email}
                                </td>
                                <td className="py-2 pr-4">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                      item.status === "sent"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {item.status}
                                  </span>
                                </td>
                                <td className="py-2 pr-4 text-gray-500 text-xs">
                                  {item.lastAttemptAt
                                    ? new Date(
                                        item.lastAttemptAt,
                                      ).toLocaleString()
                                    : "-"}
                                </td>
                                <td className="py-2 text-red-600 text-xs max-w-xs truncate">
                                  {item.error || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default CampaignMonitorPage;
